import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction
} from 'react';
import {
  IComplianceGroup,
  IEvaluatedJurisdictionView,
  ReportingPeriodType,
  UserTrackerData,
  UserTrackerDataRequest
} from '@/@types/client-api';
import Modal, { ModalProps } from '@/components/ui/Modal/Modal';
import ComplianceForm, {
  ComplianceFormFields
} from '@/components/ui/CreditTracker/Forms/ComplianceForm';
import useComplianceModal from '@/components/ui/CreditTracker/hooks/useComplianceModalHook';
import Icon from '@/components/ui/Icon';
import ExperienceLevelSelector from '@/components/ui/CreditTracker/snippets/ExperienceLevelSelector';
import {
  ComplianceModalStep,
  CompliancePeriodValue,
  SpecialStatusSelector
} from '../util/constants';
import RichText from '@/components/cms/RichText';

const ComplianceModalStepContent = lazy(
  () => import('@/components/ui/CreditTracker/Modals/ComplianceModalStepContent')
);

type ComplianceModalStepContent = {
  icon?: string;
  heading?: string;
  messagingContentArea?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  requiredFieldHelpText?: string;
  jurisdictionDisclaimer?: string;
};

export type ComplianceModalCopy = {
  explanationStep: ComplianceModalStepContent;
  experienceSwitchStep?: ComplianceModalStepContent & {
    experienceSwitcherLabel?: string;
  };
  formStep: ComplianceModalStepContent;
};

const defaultComplianceModalStepSequence = [
  ComplianceModalStep.Explanation,
  ComplianceModalStep.ExperienceSwitch,
  ComplianceModalStep.Form
];

const defaultComplianceModalCopy: ComplianceModalCopy = {
  explanationStep: {
    heading: `We can't calculate credits without your compliance period.`,
    icon: 'upcoming',
    messagingContentArea:
      'Your credit tracker setup is almost done; we just need some missing details from you.',
    primaryButtonText: 'Continue'
  },
  experienceSwitchStep: {
    heading: 'Confirm your details',
    experienceSwitcherLabel: 'Are you newly-admitted or experienced? *',
    requiredFieldHelpText: '* Indicates required field',
    primaryButtonText: 'Continue',
    jurisdictionDisclaimer:
      '<p>Attorneys admitted to the New York State Bar for two years or less are considered newly-admitted. For further details, please consult the <a href="http://ww2.nycourts.gov/attorneys/cle/index.shtml">New York State Continuing Legal Education Board</a> official website.</p>'
  },
  formStep: {
    heading: 'Add compliance information',
    primaryButtonText: 'Save',
    requiredFieldHelpText: '* Indicates required field'
  }
};

export interface ComplianceModalProps extends ModalProps {
  customerSk: number;
  isMissingCompliancePeriod: boolean;
  copy?: Partial<ComplianceModalCopy>;
  eyebrowText?: string;
  jurisdiction?: IEvaluatedJurisdictionView;
  trackerLicense?: UserTrackerData;
  complianceFormFields?: ComplianceFormFields;
  complianceGroups?: IComplianceGroup[] | null;
  selectedPeriod?: CompliancePeriodValue | null;
  specialStatuses?: SpecialStatusSelector[];
  isTransitionalOrNewlyAdmittedRegion?: boolean;
  lastTrackerRequest?: UserTrackerDataRequest;
  setIsLoading?: (loading: boolean) => void;
  onComplete?: (compliancePeriod?: ComplianceFormFields) => void;
  onSecondaryButtonPress?: () => void;
  useBackButtonNavigation?: boolean;
  modalStepSequence?: ComplianceModalStep[];
  showStepNumbers?: boolean;
  reportingPeriodOverride?: ReportingPeriodType;
  commitSpecialStatusOnFormSubmit?: boolean;
  onSpecialStatusChange?: (
    statuses: SpecialStatusSelector[]
  ) => ComplianceFormFields | undefined | Promise<ComplianceFormFields | undefined>;
}

const ComplianceModal = memo((props: ComplianceModalProps): JSX.Element => {
  const closeInFlightRef = useRef(false);
  const [isClosing, setIsClosing] = useState(false);
  const {
    open,
    setOpen,
    eyebrowText,
    trackerLicense,
    complianceFormFields,
    complianceGroups,
    selectedPeriod,
    specialStatuses,
    isTransitionalOrNewlyAdmittedRegion,
    lastTrackerRequest,
    setIsLoading,
    onComplete,
    customerSk,
    isMissingCompliancePeriod,
    copy,
    onSecondaryButtonPress,
    useBackButtonNavigation,
    reportingPeriodOverride,
    commitSpecialStatusOnFormSubmit,
    onSpecialStatusChange,
    showStepNumbers = true,
    modalStepSequence = defaultComplianceModalStepSequence
  } = props;

  const baseCopy = useMemo<ComplianceModalCopy>(
    () => ({
      ...defaultComplianceModalCopy,
      formStep: {
        ...defaultComplianceModalCopy.formStep,
        heading: isMissingCompliancePeriod
          ? 'Add compliance information'
          : 'Edit compliance period',
        primaryButtonText: 'Save'
      }
    }),
    [isMissingCompliancePeriod]
  );

  const modalCopy = useMemo<ComplianceModalCopy>(
    () => ({
      ...baseCopy,
      explanationStep: {
        ...baseCopy.explanationStep,
        ...copy?.explanationStep
      },
      experienceSwitchStep: {
        ...baseCopy.experienceSwitchStep,
        ...copy?.experienceSwitchStep
      },
      formStep: {
        ...baseCopy.formStep,
        ...copy?.formStep
      }
    }),
    [baseCopy, copy]
  );

  const {
    compliancePeriod,
    handleChange,
    handleResetOnClose,
    handleResetComplianceDates,
    handleSaveButton,
    reportingPeriod,
    fieldsWithErrors,
    setFieldsWithErrors,
    isSubmitting,
    isUpdatingDateRange,
    isUpdatingForm,
    showReset,
    selectedRegion,
    stepIndex,
    setStepIndex,
    specialStatus: {
      handleSelectorChange,
      handleSpecialStatusValidation,
      setStatuses,
      showSpecialStatusError,
      statuses
    }
  } = useComplianceModal({
    customerSk,
    complianceFormFields,
    trackerLicense,
    isMissingCompliancePeriod,
    initialSpecialStatuses: specialStatuses,
    lastTrackerRequest,
    setIsLoading,
    reportingPeriodOverride,
    commitSpecialStatusOnFormSubmit,
    onSpecialStatusChange,
    setOpen,
    open
  });

  const formContent = useMemo(
    () => (
      <ComplianceForm
        jurisdiction={selectedRegion?.jurisdiction}
        currentCompliancePeriod={compliancePeriod}
        complianceGroups={complianceGroups ?? []}
        onChange={handleChange}
        onResetDates={handleResetComplianceDates}
        reportingPeriod={reportingPeriod}
        fieldsWithErrors={fieldsWithErrors}
        setFieldsWithErrors={setFieldsWithErrors}
        isUpdatingDateInterval={isUpdatingDateRange}
        isUpdatingForm={isUpdatingForm}
        showReset={showReset}
        selectedPeriod={selectedPeriod}
      />
    ),
    [
      complianceGroups,
      compliancePeriod,
      fieldsWithErrors,
      handleChange,
      handleResetComplianceDates,
      isUpdatingDateRange,
      isUpdatingForm,
      reportingPeriod,
      selectedPeriod,
      selectedRegion?.jurisdiction,
      setFieldsWithErrors,
      showReset
    ]
  );

  const experienceSwitchContent = useMemo(
    () => (
      <>
        {isTransitionalOrNewlyAdmittedRegion && (
          <div className="mb-4">{modalCopy.experienceSwitchStep?.experienceSwitcherLabel}</div>
        )}
        {specialStatuses && (
          <ExperienceLevelSelector
            statuses={statuses}
            onChange={handleSelectorChange}
            selectorSize="large"
            showError={showSpecialStatusError}
          />
        )}
        {!!modalCopy.experienceSwitchStep?.jurisdictionDisclaimer && (
          <RichText
            propertyName="JurisdictionDisclaimer"
            className="text-2 mt-8 font-normal text-gray-dark"
            content={modalCopy.experienceSwitchStep?.jurisdictionDisclaimer}
          />
        )}
      </>
    ),
    [
      handleSelectorChange,
      isTransitionalOrNewlyAdmittedRegion,
      modalCopy.experienceSwitchStep?.experienceSwitcherLabel,
      modalCopy.experienceSwitchStep?.jurisdictionDisclaimer,
      showSpecialStatusError,
      specialStatuses,
      statuses
    ]
  );

  const handleIncrementStep = useCallback(() => {
    setStepIndex(prev => prev + 1);
  }, [setStepIndex]);

  const handleDecrementStep = useCallback(() => {
    setStepIndex(prev => {
      if (prev > 0) return prev - 1;
      return prev;
    });
  }, [setStepIndex]);

  const handleFormSave = useCallback(async () => {
    const res = await handleSaveButton();
    if (res && onComplete) {
      onComplete(compliancePeriod);
    }
  }, [compliancePeriod, handleSaveButton, onComplete]);

  const handleOpenChange = useCallback(
    (nextOpen: SetStateAction<boolean>) => {
      const resolvedOpen = typeof nextOpen === 'function' ? nextOpen(open) : nextOpen;

      if (resolvedOpen) {
        setOpen(true);
        return;
      }

      if (isUpdatingDateRange || closeInFlightRef.current) return;

      closeInFlightRef.current = true;
      setIsClosing(true);
      void handleResetOnClose()
        .then(rollbackSucceeded => rollbackSucceeded && setOpen(false))
        .finally(() => {
          closeInFlightRef.current = false;
          setIsClosing(false);
        });
    },
    [handleResetOnClose, isUpdatingDateRange, open, setOpen]
  );

  const steps = useMemo(() => {
    const getStepNumberText = (stepNumber: number) =>
      !!showStepNumbers &&
      (specialStatuses?.length ?? 0) > 0 && (
        <div className="text-2 mb-2 font-normal text-black">{`${stepNumber} of 2`}</div>
      );
    const getBackButtonPress = (stepCount: number) =>
      useBackButtonNavigation && stepCount ? handleDecrementStep : undefined;

    const stepContentByStep: Record<
      ComplianceModalStep,
      (stepCount: number, countedStepNumber: number) => JSX.Element | null
    > = {
      [ComplianceModalStep.Explanation]: stepCount =>
        isMissingCompliancePeriod ? (
          <ComplianceModalStepContent
            handleSaveButtonPress={handleIncrementStep}
            saveButtonLabel={modalCopy.explanationStep.primaryButtonText ?? ''}
            secondaryButtonText={modalCopy.explanationStep.secondaryButtonText}
            handleSecondaryButton={onSecondaryButtonPress}
            content={<MissingComplianceContent copy={modalCopy} />}
            onBackButtonPress={getBackButtonPress(stepCount)}
            isMultiStep
            isSubmitting={isSubmitting || false}
          />
        ) : null,
      [ComplianceModalStep.ExperienceSwitch]: (stepCount, countedStepNumber) =>
        isMissingCompliancePeriod && specialStatuses?.length ? (
          <ComplianceModalStepContent
            eyebrow={{
              size: 'large',
              text: (
                <div>
                  {getStepNumberText(countedStepNumber)}
                  {eyebrowText}
                </div>
              )
            }}
            title={modalCopy.experienceSwitchStep?.heading}
            subtitle={modalCopy.experienceSwitchStep?.requiredFieldHelpText}
            handleSaveButtonPress={handleSpecialStatusValidation}
            saveButtonLabel={modalCopy.experienceSwitchStep?.primaryButtonText ?? ''}
            content={experienceSwitchContent}
            isMultiStep
            isSubmitting={!!isSubmitting || !!isUpdatingForm}
            onBackButtonPress={getBackButtonPress(stepCount)}
          />
        ) : null,
      [ComplianceModalStep.Form]: (stepCount, countedStepNumber) => (
        <ComplianceModalStepContent
          eyebrow={
            stepCount
              ? {
                  size: 'large',
                  text: (
                    <div>
                      {isMissingCompliancePeriod && getStepNumberText(countedStepNumber)}
                      {eyebrowText}
                    </div>
                  )
                }
              : undefined
          }
          title={modalCopy.formStep.heading}
          subtitle={modalCopy.formStep.requiredFieldHelpText}
          saveButtonLabel={modalCopy.formStep.primaryButtonText ?? ''}
          handleSaveButtonPress={handleFormSave}
          content={formContent}
          isSubmitting={!!isSubmitting || !!isUpdatingDateRange || isClosing}
          onBackButtonPress={getBackButtonPress(stepCount)}
          isMultiStep
        />
      )
    };

    const orderedSteps = (
      modalStepSequence.length ? modalStepSequence : defaultComplianceModalStepSequence
    ).reduce<{ steps: JSX.Element[]; countedSteps: number }>(
      (result, step) => {
        const countedStepNumber =
          step === ComplianceModalStep.Explanation ? result.countedSteps : result.countedSteps + 1;
        const content = stepContentByStep[step]?.(result.steps.length, countedStepNumber);

        if (content) {
          result.steps.push(content);
          result.countedSteps = countedStepNumber;
        }

        return result;
      },
      { steps: [], countedSteps: 0 }
    ).steps;

    return orderedSteps.length
      ? orderedSteps
      : [stepContentByStep[ComplianceModalStep.Form](0, 1)!];
  }, [
    modalStepSequence,
    showStepNumbers,
    specialStatuses,
    useBackButtonNavigation,
    handleDecrementStep,
    isMissingCompliancePeriod,
    handleIncrementStep,
    modalCopy,
    onSecondaryButtonPress,
    isSubmitting,
    eyebrowText,
    handleSpecialStatusValidation,
    experienceSwitchContent,
    isUpdatingForm,
    handleFormSave,
    formContent,
    isUpdatingDateRange,
    isClosing
  ]);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      if (specialStatuses) {
        if (!commitSpecialStatusOnFormSubmit) {
          setStatuses(specialStatuses);
        }
      }
    } else {
      setFieldsWithErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitSpecialStatusOnFormSubmit, open]);

  const currentStep = useMemo(
    () => steps[Math.min(stepIndex, steps.length - 1)],
    [stepIndex, steps]
  );

  return (
    <Modal open={open} setOpen={handleOpenChange}>
      <Suspense fallback={null}>{currentStep}</Suspense>
    </Modal>
  );
});

const MissingComplianceContent = ({ copy }: { copy: ComplianceModalCopy }) => (
  <div className="space-y-8">
    <div className="mx-auto flex size-[300px] items-center justify-center rounded-full bg-gray-lightest">
      <div className="flex size-[120px] items-center justify-center rounded-full bg-gray-darker">
        <Icon name={copy.explanationStep?.icon || 'upcoming'} size="jumbo" className="text-white" />
      </div>
    </div>
    <div className="space-y-4">
      {!!copy.explanationStep.heading && (
        <h4 className="heading-4">{copy.explanationStep.heading}</h4>
      )}
      {!!copy.explanationStep.messagingContentArea && (
        <RichText
          propertyName="MessagingContentArea"
          content={copy.explanationStep.messagingContentArea}
          className="text-1-small !font-normal"
        />
      )}
    </div>
  </div>
);

ComplianceModal.displayName = 'ComplianceModal';
export default ComplianceModal;
